import { money } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const pct = (v) => (v == null ? "–" : `${v}%`);
const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between gap-4 ${strong ? "font-semibold text-base pt-2 border-t border-[#F0EBE1]" : ""}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);

export const ProfitPanel = ({ p }) => (
  <div data-testid={ADMIN.profit} className="min-w-0 bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5 text-sm space-y-1.5">
    <p className="font-heading font-bold text-base mb-2">Gross profit</p>
    {p.orders_counted ? (
      <>
        <Row label={`Revenue (${p.orders_counted} order${p.orders_counted === 1 ? "" : "s"})`} value={money(p.revenue)} />
        <Row label="Product costs" value={`− ${money(p.product_cost)}`} />
        <Row label={`Shipping${p.estimated_shipping_orders ? " (some estimated)" : ""}`} value={`− ${money(p.shipping_cost)}`} />
        <Row label="Stripe payment fees" value={p.missing_fee_orders === p.orders_counted ? "not known yet" : `− ${money(p.payment_fees)}`} />
        <Row strong label={`Gross profit · ${pct(p.margin)}`} value={money(p.gross_profit)} />
        <p className="text-xs text-stone-500">Average {money(p.avg_per_order)} per order.</p>
      </>
    ) : (
      <p className="text-stone-500">Add your cost per unit to products (Products tab → "Specs, cost & photos") to see profit here.</p>
    )}
    {p.missing_cost_orders > 0 && <p className="text-xs text-amber-700">{p.missing_cost_orders} order{p.missing_cost_orders === 1 ? "" : "s"} left out because a product in it has no cost entered.</p>}
    {p.missing_fee_orders > 0 && <p className="text-xs text-stone-500">Stripe fee unknown for {p.missing_fee_orders} counted order{p.missing_fee_orders === 1 ? "" : "s"}.</p>}
    <p className="text-xs text-stone-400">Gross profit = sale − product cost − shipping − payment fees. It is not net profit: ads, refunds and running costs aren't included.</p>
  </div>
);

export const ChatStatsPanel = ({ c }) => (
  <div data-testid={ADMIN.chatStats} className="min-w-0 bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5 text-sm">
    <p className="font-heading font-bold text-base">Kiddee chat results</p>
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
      {[["chats", "Chats", c.chats, null], ["adds", "Chats with add-to-cart", c.chats_with_add, c.add_rate], ["orders", "Orders after chatting", c.orders, c.order_rate], ["sales", "Sales after chatting", money(c.sales), null]].map(([k, label, n, r]) => (
        <div key={k} data-testid={ADMIN.chatStat(k)} className="bg-[#FDFBF7] rounded-xl p-2">
          <p className="font-heading text-xl font-extrabold">{n}</p>
          <p className="text-[11px] text-stone-500 leading-tight">{label}</p>
          {r != null && <p className="text-[11px] text-[#E06D44]">{pct(r)} of chats</p>}
        </div>
      ))}
    </div>
    <p className="mt-3 text-xs text-stone-400">Add-to-cart counts taps on products Kiddee suggested. Orders count purchases within 7 days of chatting.</p>
  </div>
);

export const FunnelPanel = ({ f, rows }) => (
  <div data-testid={ADMIN.funnel} className="min-w-0 bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5 text-sm">
    <p className="font-heading font-bold text-base">Shopping funnel</p>
    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
      {[["Product views", f.product_view, null], ["Add to cart", f.add_to_cart, f.add_rate], ["Checkouts", f.begin_checkout, f.checkout_rate], ["Purchases", f.purchase, f.purchase_rate]].map(([label, n, r]) => (
        <div key={label} className="bg-[#FDFBF7] rounded-xl p-2">
          <p className="font-heading text-xl font-extrabold">{n}</p>
          <p className="text-[11px] text-stone-500 leading-tight">{label}</p>
          {r != null && <p className="text-[11px] text-[#E06D44]">{pct(r)} of previous</p>}
        </div>
      ))}
    </div>
    <p className="mt-5 font-medium">Top products by views</p>
    {rows.length === 0 ? <p className="mt-2 text-stone-500">No product views yet.</p> : (
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-xs sm:text-sm">
          <thead className="text-stone-500"><tr><th className="py-1 pr-2">Product</th><th className="px-1">Views</th><th className="px-1">Adds</th><th className="px-1">Add rate</th><th className="px-1">Sold</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.slug} data-testid={ADMIN.insightRow(r.slug)} className="border-t border-[#F0EBE1]">
                <td className="py-1.5 pr-2 max-w-[160px] truncate">{r.name}</td><td className="px-1">{r.views}</td><td className="px-1">{r.adds}</td><td className="px-1">{pct(r.add_rate)}</td><td className="px-1">{r.purchases}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
